// components/Sidebar.tsx
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
} from '@mui/material';
import {
  sidebarContainerSx,
  sidebarHeaderSx,
  sidebarLogoWrapperSx,
  sidebarNavItemSx,
  sidebarNavItemActiveSx,
  sidebarNavBadgeSx,
} from '../../theme/recipes/navigation';
import {
  Dashboard as DashboardIcon,
  MedicalServices as MedicalIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  People as PeopleIcon,
  Category as CategoryIcon,
  Security as SecurityIcon,
  Payment as PaymentIcon,
  Business as BusinessIcon,
  LocalHospital as HospitalIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuthStore } from '../../stores/authStore';
import { useUIStore } from '../../stores/uiStore';
import { useSubscription } from '../../hooks/useSubscription';
import MediCareLogo from '../common/MediCareLogo';
import { RoleCode } from '../../types/enums/roleCodes';
import UpcomingAppointmentsSlider from './UpcomingAppointmentsSlider';

interface SidebarItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
}

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isSpecialtyActive } = useAuthStore();
  const { toggleSidebar } = useUIStore();

  // Menú para administradores
  const adminMenuItems: SidebarItem[] = [
    {
      path: '/',
      label: 'Dashboard',
      icon: <DashboardIcon />,
    },
    {
      path: '/institutions',
      label: 'Instituciones',
      icon: <BusinessIcon />,
    },
    {
      path: '/specialties',
      label: 'Especialidades',
      icon: <MedicalIcon />,
    },
    {
      path: '/roles',
      label: 'Roles',
      icon: <SecurityIcon />,
    },
    {
      path: '/users',
      label: 'Usuarios',
      icon: <PeopleIcon />,
    },
  ];

  // Menú para doctores
  const doctorMenuItems: SidebarItem[] = [
    {
      path: '/',
      label: 'Dashboard',
      icon: <DashboardIcon />,
    },
    {
      path: '/medical-records',
      label: 'Historias Clínicas',
      icon: <MedicalIcon />,
    },
    {
      path: '/appointments',
      label: 'Turnos',
      icon: <EventIcon />,
    },
  ];

  // Suscripción deshabilitada temporalmente: acceso completo para todos
  const hasActiveSubscription = true;

  // Seleccionar menú según el rol del usuario y estado de suscripción
  const menuItems = React.useMemo(() => {
    if (user?.role?.code === RoleCode.ADMIN) {
      return adminMenuItems;
    }

    // Si no tiene suscripción activa, solo mostrar suscripción
    if (!hasActiveSubscription) {
      return [];
    }

    return doctorMenuItems;
  }, [user?.role?.code, hasActiveSubscription]);

  const bottomMenuItems: SidebarItem[] = React.useMemo(() => {
    const items: SidebarItem[] = [];

    // Si no tiene suscripción activa, solo mostrar suscripción
    if (!hasActiveSubscription && user?.role?.code !== RoleCode.ADMIN) {
      return [
        {
          path: '/subscription',
          label: 'Suscripción',
          icon: <PaymentIcon />,
        },
      ];
    }

    // Menú normal para usuarios con suscripción activa o admin
    items.push(
      {
        path: '/profile',
        label: 'Perfil',
        icon: <PersonIcon />,
      },
      {
        path: '/settings',
        label: 'Configuración',
        icon: <SettingsIcon />,
      }
    );

    // Solo mostrar suscripción para doctores (no admin)
    if (user?.role?.code !== RoleCode.ADMIN) {
      items.push({
        path: '/subscription',
        label: 'Suscripción',
        icon: <PaymentIcon />,
      });
    }

    return items;
  }, [hasActiveSubscription, user?.role?.code]);

  const handleNavigation = (path: string) => {
    navigate(path);
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 900) {
      toggleSidebar();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const SidebarItem: React.FC<{ item: SidebarItem }> = ({ item }) => {
    const isActive = location.pathname === item.path;

    return (
      <motion.div
        whileHover={{ x: 4 }}
        whileTap={{ scale: 0.95 }}
        transition={{ duration: 0.1 }}
      >
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => handleNavigation(item.path)}
            sx={isActive ? sidebarNavItemActiveSx : sidebarNavItemSx}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontWeight: isActive ? 600 : 500,
              }}
            />
            {item.badge && (
              <Box sx={sidebarNavBadgeSx}>
                {item.badge}
              </Box>
            )}
          </ListItemButton>
        </ListItem>
      </motion.div>
    );
  };

  return (
    <Box sx={sidebarContainerSx}>
      {/* Header */}
      <Box sx={sidebarHeaderSx}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Box sx={sidebarLogoWrapperSx}>
            <MediCareLogo variant="full" width={200} height={56} />
          </Box>
        </motion.div>
      </Box>

      {/* Próximos Turnos Slider y separación - Solo para doctores con suscripción activa y especialidad activa */}
      {user?.role?.code === RoleCode.DOCTOR &&
        hasActiveSubscription &&
        isSpecialtyActive && (
          <>
            <UpcomingAppointmentsSlider />
            <Divider sx={{ my: 2 }} />
          </>
        )}

      {/* Navigation Menu */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 2 }}>
        <List>
          {menuItems.map((item, index) => (
            <motion.div
              key={item.path}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <SidebarItem item={item} />
            </motion.div>
          ))}
        </List>

        {/* Solo mostrar divider si hay menú principal */}
        {menuItems.length > 0 && <Divider sx={{ my: 2 }} />}

        <List>
          {bottomMenuItems.map((item, index) => (
            <motion.div
              key={item.path}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.3,
                delay: (index + menuItems.length) * 0.1,
              }}
            >
              <SidebarItem item={item} />
            </motion.div>
          ))}
        </List>
      </Box>

      {/* Logout Button */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <motion.div
          whileHover={{ x: 4 }}
          whileTap={{ scale: 0.95 }}
          transition={{ duration: 0.1 }}
        >
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                borderRadius: 2,
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'error.light',
                  color: 'error.contrastText',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText
                primary="Cerrar Sesión"
                primaryTypographyProps={{ fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>
        </motion.div>
      </Box>
    </Box>
  );
};

export default Sidebar;
